import { z } from 'zod'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { normalizePostalCode } from '@/shared/utils/polishBusinessIds'
import {
  LegalForm,
  OpeningHours,
  RestaurantSettings,
  RestaurantAddress,
  RestaurantContact,
  RestaurantIdentity,
  openingHoursSchema,
  restaurantAddressSchema,
  restaurantContactSchema,
  restaurantIdentitySchema
} from './settings'

/**
 * The restaurant settings as the settings form holds them. The same document,
 * except that a select holds its pick (`{ label, value }`), as every select in
 * DynamicForm does.
 */
export interface RestaurantSettingsFormValues {
  identity: Omit<RestaurantIdentity, 'legalForm'> & { legalForm: OptionType | null }
  address: Omit<RestaurantAddress, 'timezone'> & { timezone: OptionType | null }
  contact: RestaurantContact
  hours: OpeningHours
}

const pickedValue = (picked: unknown) =>
  (picked as OptionType | null)?.value ?? undefined

/**
 * Checks the form and hands back the document's parts as they are stored: a
 * pick becomes the value it stands for, and in a venue on Warsaw time five
 * bare digits become a postcode with its dash (only in Poland are they a code
 * missing one; a Berlin venue's 10115 is already whole).
 */
export const restaurantSettingsFormSchema = z.object({
  identity: restaurantIdentitySchema.extend({
    legalForm: z.preprocess(pickedValue, restaurantIdentitySchema.shape.legalForm)
  }),
  address: restaurantAddressSchema
    .extend({
      timezone: z.preprocess(
        picked => pickedValue(picked) ?? '',
        restaurantAddressSchema.shape.timezone
      )
    })
    .transform(address =>
      address.timezone === 'Europe/Warsaw'
        ? { ...address, postalCode: normalizePostalCode(address.postalCode) }
        : address
    ),
  contact: restaurantContactSchema,
  hours: openingHoursSchema
})

export type RestaurantSettingsFormOutput = z.output<
  typeof restaurantSettingsFormSchema
>

/** The document as the form starts from it. */
export const toFormValues = (
  settings: RestaurantSettings,
  legalFormLabel: (form: LegalForm) => string
): RestaurantSettingsFormValues => {
  const { legalForm } = settings.identity
  const { timezone } = settings.address
  return {
    identity: {
      ...settings.identity,
      legalForm: legalForm
        ? { label: legalFormLabel(legalForm), value: legalForm }
        : null
    },
    address: {
      ...settings.address,
      timezone: timezone ? { label: timezone, value: timezone } : null
    },
    contact: settings.contact,
    hours: settings.hours
  }
}
